'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import ServiceCard from '@/app/homepage/components/ServiceCard';
import ProjectCard from '@/app/homepage/components/ProjectCard';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Navigation from '@/components/common/Navigation';
import { rfpService, RfpFormData } from '@/lib/services/rfpService';

export default function TelecommunicationsServicesPage() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<RfpFormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectType: 'telecom',
    message: '',
    servicePage: 'telecommunications',
  });

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));
    
    return () => observer?.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    const result = await rfpService.submitInquiry(formData);
    if (result.success) {
      setSubmitStatus('success');
      setFormData({ name: '', company: '', email: '', phone: '', projectType: 'telecom', message: '', servicePage: 'telecommunications' });
    } else {
      setSubmitStatus('error');
      setErrorMessage(result.error || 'Failed to submit inquiry.');
    }
    setIsSubmitting(false);
  };

  return (
    <main className="min-h-screen bg-background">
      <Navigation />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center border-b border-border">
        <div className="absolute inset-0">
          <img
            src="/assets/images/tele_coms_2_-1786137261191.jpg"
            alt="East Coast Underground trucks and equipment on a grassy telecommunications jobsite"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <Link 
            href="/homepage" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 reveal"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back to Home
          </Link>
          
          <div className="mb-6 reveal" style={{ transitionDelay: '100ms' }}>
            <Icon name="SignalIcon" size={64} className="text-secondary mx-auto mb-6" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground mb-6 reveal" style={{ transitionDelay: '200ms' }}>
            Telecommunications
            <br />
            <span className="text-gradient-orange">Infrastructure</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 reveal" style={{ transitionDelay: '300ms' }}>
            Precision horizontal directional drilling for fiber optic networks, 5G infrastructure, and broadband expansion. 
            Connecting communities with minimal surface disruption.
          </p>
          
          <Link
            href="/contact"
            className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all reveal inline-block"
            style={{ transitionDelay: '400ms' }}
          >
            Request Telecom Project Quote
          </Link>
        </div>
      </section>

      {/* Equipment Specifications */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Equipment & Capabilities
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Precision Drilling Technology
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Specialized equipment designed for telecommunications infrastructure with precision locating and minimal environmental impact.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ServiceCard 
              icon="CogIcon"
              title="Ditch Witch JT10 Compact Drill"
              description="Advanced drilling system optimized for urban confined telecommunications conduit installation."
              specs={[
                'Thrust/Pullback: 10,000 lbs',
                'Torque: 1,100 ft-lbs',
                'Conduit diameter: 1.25" to 4"',
                'Max bore length: 340 feet',
                'Ideal for: Fiber optic, 5G infrastructure'
              ]}
              delay={100}
            />
            
            <ServiceCard 
              icon="CogIcon"
              title="Vermeer D24x40 S3 Navigator"
              description="Compact drilling platform with advanced locating technology for rural telecommunications projects."
              specs={[
                'Thrust/Pullback: 24,000 lbs',
                'Torque: 4,000 ft-lbs',
                'Conduit diameter: 1.25" to 12" bundles',
                'Max bore length: 600 feet',
                'Ideal for: Rural fiber networks, cable TV'
              ]}
              delay={200}
            />
            
            <ServiceCard 
              icon="MapPinIcon"
              title="Electromagnetic Locating Systems"
              description="State-of-the-art locating technology for precision drilling near existing telecommunications infrastructure."
              specs={[
                'Real-time 3D bore path visualization',
                'Sub-inch accuracy positioning',
                'Existing utility detection',
                'Depth tracking to 200 feet',
                'Integration with GIS mapping systems'
              ]}
              delay={300}
            />
            
            <ServiceCard 
              icon="CogIcon"
              title="Conduit Installation Systems"
              description="Specialized equipment for installing telecommunications conduit with future expansion capacity."
              specs={[
                'Multi-duct conduit installation',
                'HDPE and PVC conduit compatibility',
                'Pull tape and rope installation',
                'Innerduct placement systems',
                'Fiber optic cable pulling equipment'
              ]}
              delay={400}
            />
          </div>
        </div>
      </section>

      {/* Services Description */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Services & Capabilities
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Telecommunications Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Comprehensive underground utility solutions for fiber optic networks, 5G infrastructure, broadband expansion, and cable TV — connecting communities with minimal surface disruption.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ServiceCard 
              icon="CogIcon"
              title="Horizontal Directional Drilling"
              description="Our primary method for trenchless installation of telecommunications conduit beneath roads, waterways, and existing infrastructure. Using Vermeer and Ditch Witch drilling rigs, we install fiber optic conduit, coaxial cable, and multi-duct bundles with bore lengths up to 1,500 feet and conduit diameters from 1.25&quot; to 12&quot; bundles. HDD allows telecom providers to expand networks through urban corridors, across highways, and beneath environmentally sensitive areas with zero surface disruption."
              specs={[
                'Fiber optic & broadband conduit installation',
                '5G small cell & backhaul infrastructure',
                'Road, railroad & waterway crossings',
                'Conduit diameters: 1.25" to 12" bundles',
                'Max bore length: 1,500 feet'
              ]}
              delay={100}
            />
            
            <ServiceCard 
              icon="BeakerIcon"
              title="Hydro-Vac Excavation"
              description="Pressurized water and industrial vacuum technology for safe, precise excavation around existing telecommunications and utility infrastructure. Hydro-vac is our go-to method for potholing and daylighting buried conduit and cables before drilling begins. It is ideal for dense urban environments with congested utility corridors, allowing us to verify existing fiber, coaxial, and copper lines without risk of damage to active network infrastructure."
              specs={[
                'Utility potholing & conduit daylighting',
                'Safe excavation near live fiber & cable',
                'Slot trenching in confined urban areas',
                'Debris removal & site cleanup',
                'Supports HDD & open trench operations'
              ]}
              delay={200}
            />
            
            <ServiceCard 
              icon="WrenchIcon"
              title="Open Trench"
              description="Traditional open-cut excavation for telecommunications installations where trenchless methods are not feasible or cost-effective. Our crews handle full-depth trenching for conduit runs, splice enclosure pits, and service drops to homes and businesses. We follow all OSHA shoring and safety requirements and restore surfaces — pavement, curbing, and landscaping — to pre-construction condition, ensuring minimal long-term impact to neighborhoods and rights-of-way."
              specs={[
                'Conduit trench & duct bank installation',
                'Fiber splice pit & vault excavation',
                'Last-mile service drops',
                'OSHA-compliant shoring & safety',
                'Full surface restoration included'
              ]}
              delay={300}
            />
            
            <ServiceCard 
              icon="MapPinIcon"
              title="Utility Locating"
              description="Accurate identification and marking of existing underground utilities before any excavation or drilling begins on telecommunications projects. We use electromagnetic locating equipment and ground-penetrating radar (GPR) to map buried fiber, coaxial, copper, gas, electric, and water lines. Precise locating protects active network infrastructure, prevents costly service outages, and keeps telecom expansion projects on schedule by eliminating unexpected conflicts underground."
              specs={[
                'Electromagnetic utility locating',
                'Ground-penetrating radar (GPR)',
                'Pre-construction utility mapping',
                'Conflict identification & reporting',
                'Supports all excavation methods'
              ]}
              delay={400}
            />
          </div>
        </div>
      </section>

      {/* Case Studies - hidden, preserved for future use */}
      <div className="hidden">
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Project Portfolio
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Telecommunications Case Studies
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Proven track record of successful fiber optic, 5G, and broadband infrastructure projects across the East Coast.
            </p>
          </div>
          
          <div className="space-y-0">
            <ProjectCard 
              title="5G Fiber Optic Network Expansion"
              location="Philadelphia, PA"
              challenge="Deploy 15 miles of fiber optic conduit throughout downtown Philadelphia to support 5G small cell network. Required 200+ street crossings without disrupting traffic or existing utilities in congested urban environment."
              solution="Executed precision HDD bores averaging 300-500 feet using Ditch Witch JT40 with real-time locating. Coordinated with city traffic management for minimal disruption. Installed 4-inch HDPE conduit with pull tape for future fiber installation."
              timeline="Completed in 60 days"
              image="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800"
              imageAlt="Urban fiber optic network installation in downtown Philadelphia with 5G infrastructure"
              delay={100}
            />
            
            <ProjectCard 
              title="Rural Broadband Initiative"
              location="Lancaster County, PA"
              challenge="Bring high-speed internet to 500 rural homes across 25 square miles of farmland. Multiple stream crossings, road crossings, and agricultural land required trenchless technology to preserve farming operations."
              solution="Deployed mobile drilling teams with Vermeer D24x40 rigs for rapid deployment. Completed 150 individual HDD bores ranging from 200 to 800 feet. Zero crop damage and maintained all farming schedules during installation."
              timeline="Completed in 90 days"
              image="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800"
              imageAlt="Rural broadband fiber optic installation across farmland in Lancaster County"
              delay={200}
            />
            
            <ProjectCard 
              title="University Campus Fiber Network"
              location="Newark, DE"
              challenge="Upgrade University of Delaware campus network with 10Gbps fiber backbone connecting 45 buildings. Required installation during academic year without disrupting classes or damaging historic campus grounds."
              solution="Night and weekend HDD operations with sound mitigation. Precision drilling beneath walkways, courtyards, and athletic fields. Installed 6-inch conduit system with 288-strand fiber optic cable. Zero landscape restoration required."
              timeline="Completed in 75 days"
              image="https://images.unsplash.com/photo-1562774053-701939374585?w=800"
              imageAlt="University campus fiber optic network installation with historic buildings in background"
              delay={300}
            />
            
            <ProjectCard 
              title="Cable TV Infrastructure Upgrade"
              location="Ocean City, MD"
              challenge="Replace aging coaxial cable infrastructure with fiber-to-the-home system for 2,000 beachfront properties. Required drilling beneath boardwalk, dunes, and environmentally sensitive coastal areas during tourist season."
              solution="Specialized coastal drilling techniques with environmental monitoring. Completed 300+ HDD bores from 100 to 600 feet. Coordinated with environmental agencies for dune protection. Zero beach closures during installation."
              timeline="Completed in 120 days"
              image="https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800"
              imageAlt="Coastal fiber optic installation beneath boardwalk and beach dunes in Ocean City"
              delay={400}
            />
          </div>
        </div>
      </section>
      </div>

      {/* Technical Expertise */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Technical Expertise
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Telecommunications Specializations
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '100ms' }}>
              <Icon name="GlobeAltIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Fiber Optic Networks</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Long-haul fiber backbone
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Metro fiber networks
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Fiber-to-the-home (FTTH)
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Dark fiber installations
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '200ms' }}>
              <Icon name="SignalIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">5G Infrastructure</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Small cell backhaul
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Distributed antenna systems
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Macro site connections
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  C-RAN infrastructure
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '300ms' }}>
              <Icon name="TvIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Cable & Broadband</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Cable TV infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  DOCSIS 3.1 upgrades
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Hybrid fiber-coax (HFC)
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Broadband expansion projects
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 reveal">
            Ready to Expand Your Network?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 reveal" style={{ transitionDelay: '100ms' }}>
            Contact our telecommunications specialists for a detailed network assessment and competitive quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center reveal" style={{ transitionDelay: '200ms' }}>
            <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all">
              Get Project Quote
            </button>
            <Link href="/homepage">
              <button className="px-8 py-4 border-2 border-foreground text-foreground rounded-lg font-bold text-lg hover:bg-foreground hover:text-background transition-all">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* RFP Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 relative">
            <button
              onClick={() => { setShowForm(false); setSubmitStatus('idle'); }}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="XMarkIcon" size={24} className="text-foreground" />
            </button>
            <h2 className="text-2xl font-bold text-foreground mb-2">Request Telecom Project Quote</h2>
            <p className="text-muted-foreground mb-6">Fill out the form below and we will respond within 48 hours.</p>
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="CheckCircleIcon" size={40} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Inquiry Submitted!</h3>
                <p className="text-muted-foreground mb-4">Thank you! We will contact you within 48 hours.</p>
                <button onClick={() => { setShowForm(false); setSubmitStatus('idle'); }} className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all">Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="John Smith" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Company *</label>
                    <input type="text" name="company" required value={formData.company} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Telecom Corp" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="john@company.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone *</label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Project Details *</label>
                  <textarea name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="Tell us about your telecom project requirements, timeline, and location..." />
                </div>
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <Icon name="ExclamationCircleIcon" size={18} className="text-destructive flex-shrink-0" />
                    <p className="text-sm text-destructive">{errorMessage}</p>
                  </div>
                )}
                <button type="submit" disabled={isSubmitting} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSubmitting ? (
                    <><svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Submitting...</>
                  ) : 'Submit RFP Inquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}